import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Package, 
  Calendar, 
  Clock, 
  User, 
  Plus, 
  Edit2, 
  Trash2, 
  Save,
  X,
  ShoppingBag,
  Search,
  Filter,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  FileText,
  Snowflake
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerOrderPickup = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isCustomer = user?.role === 'customer';
  const isDistributor = user?.role === 'distributor';
  const canCreatePickup = !isCustomer && !isDistributor; // Only Admin/Manager/Staff can create pickups
  const [customers, setCustomers] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPickup, setEditingPickup] = useState(null);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [inventoryAvailability, setInventoryAvailability] = useState(null);
  const [inventoryWarnings, setInventoryWarnings] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [frozenVariants, setFrozenVariants] = useState([]);

  const initialFormData = {
    customer_id: '',
    pickup_date: new Date().toISOString().split('T')[0],
    pickup_time: new Date().toTimeString().slice(0, 5),
    extra_large_crates: 0,
    extra_large_pieces: 0,
    extra_large_crack_crates: 0,
    extra_large_crack_pieces: 0,
    large_crates: 0,
    large_pieces: 0,
    large_crack_crates: 0,
    large_crack_pieces: 0,
    medium_crates: 0,
    medium_pieces: 0,
    medium_crack_crates: 0,
    medium_crack_pieces: 0,
    small_crates: 0,
    small_pieces: 0,
    small_crack_crates: 0,
    small_crack_pieces: 0,
    damaged_crates: 0,
    damaged_pieces: 0,
    notes: ''
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!isCustomer && !isDistributor) {
      // Admin/Manager/Staff can fetch customers list and inventory
      fetchCustomers();
      fetchInventoryAvailability();
    } else if (isDistributor) {
      // Distributors can see their affiliated customers
      fetchAffiliatedCustomers();
    } else {
      // Mark pickup notifications as read when customer visits this page
      markPickupNotificationsAsRead();
    }
    fetchPickups();
  }, [filterDate, filterCustomer]);

  useEffect(() => {
    const fetchFrozenVariants = async () => {
      try {
        const response = await fetch(`${API_URL}/api/frozen-chicken/variants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) setFrozenVariants(await response.json());
      } catch (error) { /* ignore */ }
    };
    fetchFrozenVariants();
  }, []);

  // Fetch affiliated customers for distributors
  const fetchAffiliatedCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/distributor/affiliated-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching affiliated customers:', error);
    }
  };

  // Mark all pickup-related notifications as read for customers
  const markPickupNotificationsAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/user-notifications/mark-type-read?type=order_pickup_completed`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      // Dispatch event to update badge immediately
      window.dispatchEvent(new Event('pickupNotificationRead'));
    } catch (error) {
      console.error('Error marking pickup notifications as read:', error);
    }
  };

  // Fetch inventory availability for warnings (only for Admin/Manager/Staff)
  const fetchInventoryAvailability = async () => {
    if (isCustomer || isDistributor) return;
    try {
      const response = await fetch(`${API_URL}/api/inventory-availability/${filterDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInventoryAvailability(data);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  // Check for inventory warnings when form data changes
  useEffect(() => {
    if (!inventoryAvailability || !showForm) {
      setInventoryWarnings([]);
      return;
    }

    const warnings = [];
    const available = inventoryAvailability.available;
    const sizes = ['extra_large', 'large', 'medium', 'small', 'damaged'];
    
    sizes.forEach(size => {
      const cratesField = `${size}_crates`;
      const piecesField = `${size}_pieces`;
      const crackCratesField = `${size}_crack_crates`;
      const crackPiecesField = `${size}_crack_pieces`;
      
      const requestedCrates = parseInt(formData[cratesField]) || 0;
      const requestedPieces = parseInt(formData[piecesField]) || 0;
      const availableCrates = available[cratesField] || 0;
      const availablePieces = available[piecesField] || 0;
      
      if (requestedCrates > availableCrates) {
        warnings.push({
          type: 'error',
          message: `${size.replace('_', ' ').toUpperCase()}: Requesting ${requestedCrates} crates but only ${availableCrates} available`
        });
      }
      if (requestedPieces > availablePieces) {
        warnings.push({
          type: 'error',
          message: `${size.replace('_', ' ').toUpperCase()}: Requesting ${requestedPieces} pieces but only ${availablePieces} available`
        });
      }
      
      // Check crack quantities too (except damaged)
      if (size !== 'damaged') {
        const requestedCrackCrates = parseInt(formData[crackCratesField]) || 0;
        const requestedCrackPieces = parseInt(formData[crackPiecesField]) || 0;
        const availableCrackCrates = available[crackCratesField] || 0;
        const availableCrackPieces = available[crackPiecesField] || 0;
        
        if (requestedCrackCrates > availableCrackCrates) {
          warnings.push({
            type: 'error',
            message: `${size.replace('_', ' ').toUpperCase()} CRACK: Requesting ${requestedCrackCrates} crates but only ${availableCrackCrates} available`
          });
        }
        if (requestedCrackPieces > availableCrackPieces) {
          warnings.push({
            type: 'error',
            message: `${size.replace('_', ' ').toUpperCase()} CRACK: Requesting ${requestedCrackPieces} pieces but only ${availableCrackPieces} available`
          });
        }
      }
    });
    
    setInventoryWarnings(warnings);
  }, [formData, inventoryAvailability, showForm]);

  const fetchCustomers = async () => {
    if (isCustomer) return; // Customers don't need the customer list
    try {
      // Use /customers/list endpoint which allows all authenticated users (including staff)
      const response = await fetch(`${API_URL}/api/customers/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.filter(c => c.is_active !== false));
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchPickups = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/customer-order-pickups`;
      const params = new URLSearchParams();
      if (filterDate) params.append('pickup_date', filterDate);
      if (filterCustomer && filterCustomer !== 'all') params.append('customer_id', filterCustomer);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPickups(data);
      }
    } catch (error) {
      console.error('Error fetching pickups:', error);
      toast.error('Failed to load pickups');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      let url = `${API_URL}/api/customer-order-pickups/export?format=${format}`;
      if (filterCustomer && filterCustomer !== 'all') {
        url += `&customer_id=${filterCustomer}`;
      }
      if (filterDate) {
        // Export for the selected date and surrounding dates
        url += `&start_date=${filterDate}&end_date=${filterDate}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to export');
        return;
      }
      
      if (format === 'csv') {
        // Download CSV file
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const dateSuffix = filterDate || new Date().toISOString().split('T')[0];
        link.download = `pickup_details_${dateSuffix}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('CSV downloaded successfully');
      } else {
        // Open PDF (HTML) in new tab for printing
        const html = await response.text();
        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        toast.success('PDF opened for printing');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Keep value as string to prevent cursor jumping
    // Conversion to number happens on submit
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ponytail: type=text keeps the mobile caret alive, so digits are filtered here instead
  const handleNumberChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value.replace(/[^0-9]/g, '') }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }

    try {
      const url = editingPickup 
        ? `${API_URL}/api/customer-order-pickups/${editingPickup.id}`
        : `${API_URL}/api/customer-order-pickups`;
      
      // Convert numeric fields to integers before submitting
      const payload = {
        ...formData,
        extra_large_crates: parseInt(formData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(formData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(formData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(formData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(formData.large_crates) || 0,
        large_pieces: parseInt(formData.large_pieces) || 0,
        large_crack_crates: parseInt(formData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(formData.large_crack_pieces) || 0,
        medium_crates: parseInt(formData.medium_crates) || 0,
        medium_pieces: parseInt(formData.medium_pieces) || 0,
        medium_crack_crates: parseInt(formData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(formData.medium_crack_pieces) || 0,
        small_crates: parseInt(formData.small_crates) || 0,
        small_pieces: parseInt(formData.small_pieces) || 0,
        small_crack_crates: parseInt(formData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(formData.small_crack_pieces) || 0,
        damaged_crates: parseInt(formData.damaged_crates) || 0,
        damaged_pieces: parseInt(formData.damaged_pieces) || 0,
        frozen_chicken_packs: frozenVariants.reduce((acc, v) => {
          const q = parseInt(formData[`frozen_${v.key}`]) || 0;
          if (q) acc[v.key] = q;
          return acc;
        }, {}),
        empty_feed_bags_dozens: parseInt(formData.empty_feed_bags_dozens) || 0
      };
      
      const response = await fetch(url, {
        method: editingPickup ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingPickup ? 'Pickup updated successfully' : 'Pickup recorded successfully');
        setFormData(initialFormData);
        setShowForm(false);
        setEditingPickup(null);
        fetchPickups();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save pickup');
      }
    } catch (error) {
      console.error('Error saving pickup:', error);
      toast.error('Failed to save pickup');
    }
  };

  const handleEdit = (pickup) => {
    if (user?.role !== 'admin') {
      toast.error('Only Admin can edit pickups');
      return;
    }
    setEditingPickup(pickup);
    setFormData({
      customer_id: pickup.customer_id,
      pickup_date: pickup.pickup_date,
      pickup_time: pickup.pickup_time,
      extra_large_crates: pickup.extra_large_crates || 0,
      extra_large_pieces: pickup.extra_large_pieces || 0,
      extra_large_crack_crates: pickup.extra_large_crack_crates || 0,
      extra_large_crack_pieces: pickup.extra_large_crack_pieces || 0,
      large_crates: pickup.large_crates || 0,
      large_pieces: pickup.large_pieces || 0,
      large_crack_crates: pickup.large_crack_crates || 0,
      large_crack_pieces: pickup.large_crack_pieces || 0,
      medium_crates: pickup.medium_crates || 0,
      medium_pieces: pickup.medium_pieces || 0,
      medium_crack_crates: pickup.medium_crack_crates || 0,
      medium_crack_pieces: pickup.medium_crack_pieces || 0,
      small_crates: pickup.small_crates || 0,
      small_pieces: pickup.small_pieces || 0,
      small_crack_crates: pickup.small_crack_crates || 0,
      small_crack_pieces: pickup.small_crack_pieces || 0,
      damaged_crates: pickup.damaged_crates || 0,
      damaged_pieces: pickup.damaged_pieces || 0,
      notes: pickup.notes || '',
      ...frozenVariants.reduce((acc, v) => {
        acc[`frozen_${v.key}`] = (pickup.frozen_chicken_packs?.[v.key] || 0);
        return acc;
      }, {}),
      empty_feed_bags_dozens: pickup.empty_feed_bags_dozens || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (pickupId) => {
    if (user?.role !== 'admin') {
      toast.error('Only Admin can delete pickups');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this pickup? This will update the customer ledger.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/customer-order-pickups/${pickupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Pickup deleted successfully');
        fetchPickups();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete pickup');
      }
    } catch (error) {
      console.error('Error deleting pickup:', error);
      toast.error('Failed to delete pickup');
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingPickup(null);
    setFormData(initialFormData);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Egg size configuration for the form
  const eggSizes = [
    { key: 'extra_large', label: 'Extra Large', color: 'purple', hasCracks: true },
    { key: 'large', label: 'Large', color: 'emerald', hasCracks: true },
    { key: 'medium', label: 'Medium', color: 'blue', hasCracks: true },
    { key: 'small', label: 'Small', color: 'amber', hasCracks: true },
    { key: 'damaged', label: 'Damaged', color: 'orange', hasCracks: false }
  ];

  // Render egg quantity input - inline to prevent re-mounting
  const renderEggInput = (sizeKey, fieldName, label, isRed = false) => (
    <div key={`${sizeKey}-${fieldName}`}>
      <Label className={`text-xs ${isRed ? 'text-red-600' : 'text-slate-600'}`}>{label}</Label>
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={formData[`${sizeKey}_${fieldName}`] || ''}
        onChange={(e) => handleNumberChange(`${sizeKey}_${fieldName}`, e.target.value)}
        className={`mt-1 ${isRed ? 'bg-red-50/50' : ''}`}
        data-testid={`input-${sizeKey}-${fieldName}`}
      />
    </div>
  );

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="customer-order-pickup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-emerald-600" />
            {isCustomer ? 'My Pick-up History' : isDistributor ? 'Affiliated Customer Pickups' : 'Customer Order Pick-up'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isCustomer 
              ? 'View your egg pick-up history' 
              : isDistributor 
                ? 'View pick-up history for your affiliated customers'
                : 'Record customer egg pickups and auto-update ledger'}
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="gap-2"
                disabled={exporting || pickups.length === 0}
                data-testid="export-pickups-btn"
              >
                {exporting ? (
                  <div className="animate-spin w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} className="gap-2 cursor-pointer">
                <FileText className="w-4 h-4 text-red-600" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canCreatePickup && (
            <Button 
              onClick={() => { setShowForm(true); setEditingPickup(null); setFormData(initialFormData); }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-new-pickup"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Pickup
            </Button>
          )}
        </div>
      </div>

      {/* Pickup Form - Only for Admin/Manager/Staff */}
      {showForm && canCreatePickup && (
        <div className="card p-6" data-testid="pickup-form">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              {editingPickup ? 'Edit Pickup' : 'Record New Pickup'}
            </h2>
            <Button variant="ghost" size="sm" onClick={cancelForm}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer, Date, Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer *
                </Label>
                <Select 
                  value={formData.customer_id} 
                  onValueChange={(value) => handleInputChange('customer_id', value)}
                >
                  <SelectTrigger className="mt-1" data-testid="select-customer">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Pickup Date *
                </Label>
                <Input
                  type="date"
                  value={formData.pickup_date}
                  onChange={(e) => handleInputChange('pickup_date', e.target.value)}
                  className="mt-1"
                  data-testid="input-pickup-date"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pickup Time *
                </Label>
                <Input
                  type="time"
                  value={formData.pickup_time}
                  onChange={(e) => handleInputChange('pickup_time', e.target.value)}
                  className="mt-1"
                  data-testid="input-pickup-time"
                />
              </div>
            </div>

            {/* Staff Making Entry (Read-only) */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Staff Recording:</span> {user?.name || 'Unknown'}
              </p>
            </div>

            {/* Inventory Availability Panel */}
            {inventoryAvailability && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Available Inventory for {formData.pickup_date}
                </h4>
                <div className="grid grid-cols-5 gap-3 text-sm">
                  {['extra_large', 'large', 'medium', 'small', 'damaged'].map(size => (
                    <div key={size} className="text-center">
                      <div className="font-medium text-xs uppercase text-blue-600 mb-1">
                        {size === 'extra_large' ? 'X-Large' : size.charAt(0).toUpperCase() + size.slice(1)}
                      </div>
                      <div className="font-bold text-blue-900">
                        {inventoryAvailability.available[`${size}_crates`] || 0}c
                      </div>
                      <div className="text-xs text-blue-700">
                        {inventoryAvailability.available[`${size}_pieces`] || 0}p
                      </div>
                      {size !== 'damaged' && (
                        <div className="text-xs text-orange-600 mt-1">
                          Crk: {inventoryAvailability.available[`${size}_crack_crates`] || 0}c / {inventoryAvailability.available[`${size}_crack_pieces`] || 0}p
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inventory Warnings */}
            {inventoryWarnings.length > 0 && (
              <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg" data-testid="inventory-warnings">
                <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Inventory Warning - Quantity Exceeds Available Stock
                </h4>
                <ul className="space-y-1">
                  {inventoryWarnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      {warning.message}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-red-600 mt-2 italic">
                  You can still proceed, but this will result in negative inventory.
                </p>
              </div>
            )}

            {/* Egg Quantities */}
            <div>
              <Label className="text-base font-semibold text-slate-700 mb-3 block">
                Egg Quantities
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {eggSizes.map(size => (
                  <div key={size.key} className={`rounded-lg border-2 border-${size.color}-200 bg-${size.color}-50/50 p-4`}>
                    <h4 className={`font-semibold text-${size.color}-800 mb-3 flex items-center gap-2`}>
                      <Package className="w-4 h-4" />
                      {size.label}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {renderEggInput(size.key, 'crates', 'Crates')}
                      {renderEggInput(size.key, 'pieces', 'Pieces')}
                      {size.hasCracks && (
                        <>
                          {renderEggInput(size.key, 'crack_crates', 'Crack Crates', true)}
                          {renderEggInput(size.key, 'crack_pieces', 'Crack Pieces', true)}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Frozen Chicken Packs */}
            {frozenVariants.length > 0 && (
              <div>
                <Label className="text-base font-semibold text-slate-700 mb-3 block flex items-center gap-2">
                  <Snowflake className="w-4 h-4 text-cyan-600" /> Frozen Chicken (packs)
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {frozenVariants.map(v => (
                    <div key={v.key} className="rounded-lg border-2 border-cyan-200 bg-cyan-50/50 p-4">
                      <h4 className="font-semibold text-cyan-800 mb-2 text-sm">{v.label || `${v.key} Pack`}</h4>
                      <Label className="text-xs text-slate-600">Packs</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData[`frozen_${v.key}`] || ''}
                        onChange={(e) => handleNumberChange(`frozen_${v.key}`, e.target.value)}
                        className="mt-1"
                        data-testid={`input-frozen-${v.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty Chicken Feed Bags */}
            <div>
              <Label className="text-base font-semibold text-slate-700 mb-3 block flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-600" /> Empty Chicken Feed Bags (dozens)
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-4">
                  <h4 className="font-semibold text-amber-800 mb-2 text-sm">Empty Feed Bags</h4>
                  <Label className="text-xs text-slate-600">Dozens</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.empty_feed_bags_dozens || ''}
                    onChange={(e) => handleNumberChange('empty_feed_bags_dozens', e.target.value)}
                    className="mt-1"
                    data-testid="input-empty-feed-bags"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional notes..."
                className="mt-1"
                rows={2}
                data-testid="input-notes"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={cancelForm}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-pickup">
                <Save className="w-4 h-4 mr-2" />
                {editingPickup ? 'Update Pickup' : 'Record Pickup'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Filters - Simplified for customers */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <Label className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4" />
              Filter by Date
            </Label>
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              data-testid="filter-date"
            />
          </div>
          {!isCustomer && (
            <div className="flex-1">
              <Label className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4" />
                Filter by {isDistributor ? 'Affiliated Customer' : 'Customer'}
              </Label>
              <Select value={filterCustomer} onValueChange={setFilterCustomer}>
                <SelectTrigger data-testid="filter-customer">
                  <SelectValue placeholder={isDistributor ? "All affiliated customers" : "All customers"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isDistributor ? 'All Affiliated Customers' : 'All Customers'}</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button 
            variant="outline" 
            onClick={() => { setFilterDate(''); setFilterCustomer('all'); }}
            data-testid="btn-clear-filters"
          >
            <X className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Pickups List */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Pickup Records
            {filterDate && <span className="text-sm font-normal text-slate-500">({formatDate(filterDate)})</span>}
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : pickups.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No pickups found for the selected filters
          </div>
        ) : (
          <div className="table-responsive">
            <table className="w-full text-sm min-w-[900px]" data-testid="pickups-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-3 text-left font-semibold">Customer</th>
                  <th className="p-3 text-left font-semibold">Date</th>
                  <th className="p-3 text-left font-semibold">Time</th>
                  <th className="p-3 text-left font-semibold">Staff</th>
                  <th className="p-3 text-center font-semibold text-purple-700">XL</th>
                  <th className="p-3 text-center font-semibold text-emerald-700">Large</th>
                  <th className="p-3 text-center font-semibold text-blue-700">Medium</th>
                  <th className="p-3 text-center font-semibold text-amber-700">Small</th>
                  <th className="p-3 text-center font-semibold text-orange-700">Damaged</th>
                  <th className="p-3 text-center font-semibold text-cyan-700">Frozen</th>
                  <th className="p-3 text-center font-semibold text-amber-700">Empty Bags</th>
                  <th className="p-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pickups.map(pickup => (
                  <tr key={pickup.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium">{pickup.customer_name}</td>
                    <td className="p-3 text-slate-600">{formatDate(pickup.pickup_date)}</td>
                    <td className="p-3 text-slate-600">{formatTime(pickup.pickup_time)}</td>
                    <td className="p-3 text-slate-600">{pickup.staff_name}</td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        <span className="font-medium">{pickup.extra_large_crates}c/{pickup.extra_large_pieces}p</span>
                        {(pickup.extra_large_crack_crates > 0 || pickup.extra_large_crack_pieces > 0) && (
                          <span className="text-red-500 block">Crk: {pickup.extra_large_crack_crates}c/{pickup.extra_large_crack_pieces}p</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        <span className="font-medium">{pickup.large_crates}c/{pickup.large_pieces}p</span>
                        {(pickup.large_crack_crates > 0 || pickup.large_crack_pieces > 0) && (
                          <span className="text-red-500 block">Crk: {pickup.large_crack_crates}c/{pickup.large_crack_pieces}p</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        <span className="font-medium">{pickup.medium_crates}c/{pickup.medium_pieces}p</span>
                        {(pickup.medium_crack_crates > 0 || pickup.medium_crack_pieces > 0) && (
                          <span className="text-red-500 block">Crk: {pickup.medium_crack_crates}c/{pickup.medium_crack_pieces}p</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        <span className="font-medium">{pickup.small_crates}c/{pickup.small_pieces}p</span>
                        {(pickup.small_crack_crates > 0 || pickup.small_crack_pieces > 0) && (
                          <span className="text-red-500 block">Crk: {pickup.small_crack_crates}c/{pickup.small_crack_pieces}p</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        <span className="font-medium">{pickup.damaged_crates}c/{pickup.damaged_pieces}p</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        {pickup.frozen_chicken_packs && Object.keys(pickup.frozen_chicken_packs).length > 0 ? (
                          frozenVariants.filter(v => pickup.frozen_chicken_packs[v.key]).map(v => (
                            <span key={v.key} className="block font-medium text-cyan-700">{v.label || v.key}: {pickup.frozen_chicken_packs[v.key]}</span>
                          ))
                        ) : <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-xs">
                        {pickup.empty_feed_bags_dozens ? (
                          <span className="font-medium text-amber-700">{pickup.empty_feed_bags_dozens} dz</span>
                        ) : <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      {user?.role === 'admin' && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(pickup)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`btn-edit-${pickup.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(pickup.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-delete-${pickup.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOrderPickup;
